import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { predefinedAgentRoles, getAgentRoleById } from '@/constants/agentRoles';
import { Agent } from '@/stores/agentStore';

interface AgentConfigurationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (agent: Partial<Agent>) => void;
  existingAgent?: Agent;
  creationMode?: 'new' | 'fromRole' | 'customize';
  initialRoleId?: string;
  onGenerateProfile?: (role: string, customizations?: any) => void;
  generatedProfile?: any;
  profileGenerationInProgress?: boolean;
}

const AgentConfigurationDialog: React.FC<AgentConfigurationDialogProps> = ({
  open,
  onOpenChange,
  onSave,
  existingAgent,
  creationMode = 'new',
  initialRoleId = '',
  onGenerateProfile,
  generatedProfile,
  profileGenerationInProgress = false
}) => {  const [activeTab, setActiveTab] = useState('role');
  const [selectedRole, setSelectedRole] = useState<string>(
    initialRoleId || existingAgent?.role || 'content-manager'
  );
  const [customName, setCustomName] = useState<string>(existingAgent?.name || '');
  const [customInstructions, setCustomInstructions] = useState<string>(existingAgent?.instructions || '');
  const [isEnabled, setIsEnabled] = useState<boolean>(existingAgent?.enabled !== false);
  const [observationList, setObservationList] = useState<string[]>(
    existingAgent?.observationList || []
  );
  const [triggerEvents, setTriggerEvents] = useState<string[]>(
    existingAgent?.triggerEvents || []
  );  const [agentType, setAgentType] = useState<string>(existingAgent?.type || 'human');
  
  // New state for AI-assisted profile creation
  const [customFocus, setCustomFocus] = useState<string[]>([]);
  const [customExpertise, setCustomExpertise] = useState<string[]>([]);
  const [customFocusInput, setCustomFocusInput] = useState('');
  const [customExpertiseInput, setCustomExpertiseInput] = useState('');
  const [isCustomizing, setIsCustomizing] = useState(creationMode === 'customize');
    // Update role-based defaults when role changes
  useEffect(() => {
    const role = getAgentRoleById(selectedRole);
    if (role?.defaultNodeTypes) {
      setObservationList(role.defaultNodeTypes);
    }
  }, [selectedRole]);
  
  // Set initial role based on creationMode and initialRoleId
  useEffect(() => {
    if (creationMode === 'fromRole' && initialRoleId) {
      setSelectedRole(initialRoleId);
    }
  }, [creationMode, initialRoleId]);
    // Handle save
  const handleSave = () => {
    // Try to get the role, or create a fallback for custom role
    let role = getAgentRoleById(selectedRole);
    
    // Handle common role ID mismatches
    if (!role) {
      // Try to normalize the role ID by removing hyphens and converting to camelCase
      const normalizedRoleId = selectedRole.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      console.log(`Attempting to find role with normalized ID: ${normalizedRoleId}`);
      role = getAgentRoleById(normalizedRoleId);
      
      // Specific handling for known mismatches
      if (!role && selectedRole === 'data-analyst') {
        console.log("Correcting 'data-analyst' to 'dataAnalyst'");
        role = getAgentRoleById('dataAnalyst');
      }
    }
    
    // Special handling for custom role to prevent errors
    if (!role && selectedRole === 'custom') {
      console.log("Using fallback custom role definition");
      role = {
        id: 'custom',
        name: 'Custom Agent',
        description: 'A fully customizable agent with user-defined capabilities.',
        capabilities: ['userDefined', 'customizable', 'adaptable'],
        defaultPrompts: {},
        defaultNodeTypes: [],
        avatarOptions: ['/images/avatars/custom-agent-1.png']
      };
    } else if (!role) {
      console.error(`Role not found: ${selectedRole}`);
      alert(`Error: Role "${selectedRole}" not found. Please select a valid role.`);
      return;
    }
    
    try {
      console.log(`Creating agent with role: ${selectedRole}, name: ${customName || role.name}`);
      
      const agentData: Partial<Agent> = {
        name: customName || role.name,
        role: selectedRole,
        type: agentType,
        enabled: isEnabled,
        instructions: customInstructions,
        triggerEvents: triggerEvents,
        observationList: observationList,
        settings: {}
      };

      // Include existing agent data if editing
      if (existingAgent) {
        agentData.id = existingAgent.id;
        agentData.createdAt = existingAgent.createdAt;
        agentData.updatedAt = new Date().toISOString();
        agentData.projectId = existingAgent.projectId;
      }
      
      console.log("Saving agent:", agentData);
      onSave(agentData);
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving agent:", error);
      alert("Error creating agent: " + (error instanceof Error ? error.message : String(error)));
    }
  };  // Get available roles
  const availableRoles = Object.values(predefinedAgentRoles);
  
  // Get current role with fallback for custom role
  let currentRole = getAgentRoleById(selectedRole);
  
  // Fallback for custom role
  if (!currentRole && selectedRole === 'custom') {
    currentRole = {
      id: 'custom',
      name: 'Custom Agent',
      description: 'A fully customizable agent with user-defined capabilities.',
      capabilities: ['userDefined', 'customizable', 'adaptable'],
      defaultPrompts: {},
      defaultNodeTypes: [],
      avatarOptions: ['/images/avatars/custom-agent-1.png']
    };
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingAgent ? 'Edit Agent' : 'Create New Agent'}
          </DialogTitle>
        </DialogHeader>
        
        {/* Show generated profile if available */}
        {generatedProfile && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <h3 className="text-sm font-medium text-green-800 mb-1">AI-Generated Profile</h3>
            <p className="text-sm text-green-700">{generatedProfile.description}</p>
            
            {generatedProfile.capabilities && generatedProfile.capabilities.length > 0 && (
              <div className="mt-2">
                <h4 className="text-xs font-medium text-green-800">Capabilities:</h4>
                <div className="flex flex-wrap gap-1 mt-1">
                  {generatedProfile.capabilities.map((cap: string, i: number) => (
                    <span key={i} className="text-xs bg-green-100 text-green-800 px-1 rounded">
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {generatedProfile.suggestedActions && generatedProfile.suggestedActions.length > 0 && (
              <div className="mt-2">
                <h4 className="text-xs font-medium text-green-800">Suggested Actions:</h4>
                <div className="flex flex-wrap gap-1 mt-1">
                  {generatedProfile.suggestedActions.map((action: any, i: number) => (
                    <span key={i} className="text-xs bg-blue-100 text-blue-800 px-1 rounded">
                      {action.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="role">Role & Identity</TabsTrigger>
            <TabsTrigger value="customization">Customization</TabsTrigger>
            <TabsTrigger value="observations">Observations</TabsTrigger>
          </TabsList>
          
          {/* Role & Identity Tab */}
          <TabsContent value="role" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="role">Agent Role</Label>
                <Select
                  value={selectedRole}
                  onValueChange={setSelectedRole}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentRole && (
                  <p className="text-sm text-gray-500 mt-1">{currentRole.description}</p>
                )}
              </div>
              
              {/* Role selection mode toggle */}
              {!existingAgent && (
                <div className="mb-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Switch
                      id="customize-role"
                      checked={isCustomizing}
                      onCheckedChange={setIsCustomizing}
                    />
                    <Label htmlFor="customize-role">Customize this role</Label>
                  </div>
                  <p className="text-xs text-gray-500">
                    {isCustomizing
                      ? "Customize this role with specific focus areas and expertise"
                      : "Use the default role configuration"}
                  </p>
                </div>
              )}
              
              <div>
                <Label htmlFor="name">Custom Name (Optional)</Label>
                <Input
                  id="name"
                  placeholder={currentRole?.name || 'Agent Name'}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank to use the default name for this role
                </p>
              </div>
                <div>
                <Label htmlFor="description">Instructions (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder={`Instructions for ${currentRole?.name || 'Agent'}`}
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  rows={3}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Provide specific instructions or context for this agent
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="enabled"
                  checked={isEnabled}
                  onCheckedChange={setIsEnabled}
                />
                <Label htmlFor="enabled">Enable Agent</Label>
              </div>
              {/* AI-assisted profile generation */}
              {onGenerateProfile && (isCustomizing || existingAgent) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium mb-2">AI-Assisted Profile Creation</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Let AI generate a tailored profile for this agent based on the selected role and customizations.
                  </p>
                  <Button
                    onClick={() => {
                      const customizations = {
                        title: customName || currentRole?.name,
                        focus: customFocus,
                        expertise: customExpertise
                      };
                      onGenerateProfile(selectedRole, customizations);
                    }}
                    disabled={profileGenerationInProgress}
                    variant="outline"
                    className="w-full"
                  >
                    {profileGenerationInProgress ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-violet-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating Profile...
                      </span>
                    ) : (
                      'Generate AI Profile'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Customization Tab */}
          <TabsContent value="customization" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="custom-title">Custom Title</Label>
                <Input
                  id="custom-title"
                  placeholder="e.g., Marketing Manager, Developer, Software Architect"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Specify a custom title for this agent
                </p>
              </div>
              
              <div>
                <Label htmlFor="focus-areas">Focus Areas</Label>
                <div className="flex items-center space-x-2 mb-2">
                  <Input
                    id="focus-areas"
                    placeholder="Add focus area"
                    value={customFocusInput}
                    onChange={(e) => setCustomFocusInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customFocusInput.trim()) {
                        e.preventDefault();
                        setCustomFocus([...customFocus, customFocusInput.trim()]);
                        setCustomFocusInput('');
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (customFocusInput.trim()) {
                        setCustomFocus([...customFocus, customFocusInput.trim()]);
                        setCustomFocusInput('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {customFocus.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {customFocus.map((focus, index) => (
                      <div key={index} className="flex items-center bg-violet-100 text-violet-800 px-2 py-1 rounded text-xs">
                        {focus}
                        <button
                          type="button"
                          className="ml-1 text-violet-600 hover:text-violet-800"
                          onClick={() => setCustomFocus(customFocus.filter((_, i) => i !== index))}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <Label htmlFor="expertise">Areas of Expertise</Label>
                <div className="flex items-center space-x-2 mb-2">
                  <Input
                    id="expertise"
                    placeholder="Add expertise"
                    value={customExpertiseInput}
                    onChange={(e) => setCustomExpertiseInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customExpertiseInput.trim()) {
                        e.preventDefault();
                        setCustomExpertise([...customExpertise, customExpertiseInput.trim()]);
                        setCustomExpertiseInput('');
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (customExpertiseInput.trim()) {
                        setCustomExpertise([...customExpertise, customExpertiseInput.trim()]);
                        setCustomExpertiseInput('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                {customExpertise.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {customExpertise.map((expertise, index) => (
                      <div key={index} className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {expertise}
                        <button
                          type="button"
                          className="ml-1 text-blue-600 hover:text-blue-800"
                          onClick={() => setCustomExpertise(customExpertise.filter((_, i) => i !== index))}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
            {/* Observations Tab */}
          <TabsContent value="observations" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label>Observation List</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Node IDs or types this agent should observe for changes
                </p>
                <Textarea
                  placeholder="Enter node IDs or types to observe, one per line"
                  value={observationList.join('\n')}
                  onChange={(e) => setObservationList(e.target.value.split('\n').filter(Boolean))}
                  rows={4}
                />
              </div>
              
              <div>
                <Label>Trigger Events</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Events that should trigger this agent
                </p>
                <Textarea
                  placeholder="Enter trigger events, one per line (e.g., node-updated, content-changed)"
                  value={triggerEvents.join('\n')}
                  onChange={(e) => setTriggerEvents(e.target.value.split('\n').filter(Boolean))}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="agent-type">Agent Type</Label>
                <Select
                  value={agentType}
                  onValueChange={setAgentType}
                >
                  <SelectTrigger id="agent-type">
                    <SelectValue placeholder="Select agent type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="human">Human</SelectItem>
                    <SelectItem value="ai">AI</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedRole} // Disable if no role is selected
          >
            {existingAgent ? 'Save Changes' : 'Create Agent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentConfigurationDialog;